/**
 * v1.14.65.0 — D&O wizard hook (state + localStorage autosave + remote save-step).
 */
import { useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";
import { parseLegacyControllateString }
  from "@/components/wizard/ControllateTableInput";

const LS_KEY = "do_draft_v2";
const BASE = `${process.env.REACT_APP_BACKEND_URL}/api/public/do`;

/**
 * Fase 4.9 — migrazione one-shot al mount:
 * se `organi.controllate_partecipate_lista` (stringa legacy) è presente
 * ma `organi.controllate` è vuoto → parse best-effort in array e svuota
 * la textarea legacy per evitare doppia sorgente.
 * Mutazione idempotente: ritorna sempre un nuovo oggetto.
 */
function migrateLegacyControllate(fd) {
  if (!fd || typeof fd !== "object") return fd || {};
  const organi = fd.organi || {};
  const hasArray = Array.isArray(organi.controllate) && organi.controllate.length > 0;
  const legacy = organi.controllate_partecipate_lista;
  if (hasArray || !legacy || typeof legacy !== "string" || !legacy.trim()) {
    return fd;
  }
  const parsed = parseLegacyControllateString(legacy);
  if (parsed.length === 0) return fd;
  return {
    ...fd,
    organi: {
      ...organi,
      controllate: parsed,
      controllate_partecipate_lista: "",
    },
  };
}

export default function useDOWizardState({ token, openMode }) {
  const [loading, setLoading] = useState(true);
  const [errCode, setErrCode] = useState(null);
  const [sezioni, setSezioni] = useState(null);
  const [wizard, setWizard] = useState(null);
  const [compagnie, setCompagnie] = useState({});
  const [status, setStatus] = useState(null);
  const [clientName, setClientName] = useState("");
  const [formData, setFormData] = useState({});
  const [wantsCyber, setWantsCyber] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [contactData, setContactData] = useState({
    client_name: "", client_email: "", client_telefono: "", company_name: "",
  });
  const saveTimer = useRef(null);
  const lsTimer = useRef(null);

  // Initial load
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const url = openMode
          ? `${BASE}/questionnaires/form-fields`
          : `${BASE}/${token}/form-fields`;
        const { data } = await axios.get(url);
        if (cancelled) return;
        setSezioni(data.sezioni);
        setWizard(data.wizard);
        setCompagnie(data.compagnie || {});
        if (openMode) {
          // try to restore draft from localStorage
          try {
            const raw = localStorage.getItem(LS_KEY);
            if (raw) {
              const parsed = JSON.parse(raw);
              if (parsed.formData) setFormData(migrateLegacyControllate(parsed.formData));
              if (parsed.contactData) setContactData(parsed.contactData);
              if (parsed.wantsCyber) setWantsCyber(parsed.wantsCyber);
              if (parsed.currentStep) setCurrentStep(parsed.currentStep);
            }
          } catch { /* ignore */ }
          setStatus({ status: "draft", submitted: false, transmitted: false });
        } else {
          setFormData(migrateLegacyControllate(data.current_form_data || {}));
          setClientName(data.client_name || "");
          setWantsCyber(!!data.wants_cyber);
          const st = await axios.get(`${BASE}/${token}/status`);
          if (cancelled) return;
          setStatus(st.data);
        }
      } catch (e) {
        const d = e.response?.data?.detail;
        if (!cancelled) setErrCode(typeof d === "object" ? d.error_code : "LOAD_ERROR");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [token, openMode]);

  // LocalStorage autosave (debounced 1.5s — bilancia perf vs no-data-loss)
  useEffect(() => {
    if (!openMode || loading) return;
    if (lsTimer.current) clearTimeout(lsTimer.current);
    lsTimer.current = setTimeout(() => {
      try {
        localStorage.setItem(LS_KEY, JSON.stringify({
          formData, contactData, wantsCyber, currentStep,
          savedAt: new Date().toISOString(),
        }));
      } catch { /* quota exceeded or disabled */ }
    }, 1500);
    return () => { if (lsTimer.current) clearTimeout(lsTimer.current); };
  }, [openMode, loading, formData, contactData, wantsCyber, currentStep]);

  const setField = useCallback((sez, campo, value) => {
    setFormData((prev) => {
      const nd = { ...prev, [sez]: { ...(prev[sez] || {}), [campo]: value } };
      // Remote autosave for broker-gated mode
      if (!openMode && token) {
        if (saveTimer.current) clearTimeout(saveTimer.current);
        saveTimer.current = setTimeout(async () => {
          setSaving(true);
          try {
            await axios.post(`${BASE}/${token}/save-step`, {
              form_data: { [sez]: { [campo]: value } },
              schema_version: "v2_full",
            });
          } catch { /* silent */ }
          finally { setSaving(false); }
        }, 1200);
      }
      return nd;
    });
  }, [openMode, token]);

  const clearDraft = useCallback(() => {
    try { localStorage.removeItem(LS_KEY); } catch { /* ignore */ }
  }, []);

  const submit = useCallback(async (toast, opts = {}) => {
    setSubmitting(true);
    try {
      if (openMode) {
        if (!contactData.client_name && !contactData.client_email) {
          toast?.error("Indica almeno nome o email per essere ricontattato");
          setSubmitting(false);
          return null;
        }
        // v1.14.67.1 — usa il nuovo endpoint /api/do/questionario (alias con PDF gen immediata + storage)
        // v1.56 (feb-2026) — click-wrap art. 1892 c.c. + MUP Reg. IVASS 40/2018
        const { data } = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/do/questionario`,
          { form_data: formData, ...contactData, wants_cyber: wantsCyber,
            schema_version: "v3_full",
            signed_declaration: !!opts.signedDeclaration,
            session_hash: opts.sessionHash || null,
            mup_visione: opts.mupVisione || null,
            consensi_granulari: opts.consensiGranulari || null,
            // v1.72.3 — Preferenze ricontatto (canale + fascia oraria)
            preferenze_ricontatto: opts.preferenzeRicontatto || null },
        );
        clearDraft();
        setStatus({ status: data.status, submitted: true,
                    transmitted: false, message: data.message,
                    id_pratica: data.id_pratica,
                    downloads: data.downloads,
                    download_token: data.download_token,
                    next_cyber_url: data.next_cyber_url,
                    wants_cyber: !!data.wants_cyber });
        setClientName(contactData.client_name || contactData.client_email);
        toast?.success(data.message || "Richiesta inviata!");
        return data;
      } else {
        if (saveTimer.current) clearTimeout(saveTimer.current);
        await axios.post(`${BASE}/${token}/save-step`, {
          form_data: formData, wants_cyber: wantsCyber,
          schema_version: "v2_full",
        });
        const { data } = await axios.post(`${BASE}/${token}/submit`);
        setStatus({ ...status, status: data.status, submitted: true });
        toast?.success(data.message || "Inviato!");
        return data;
      }
    } catch (e) {
      const det = e.response?.data?.detail;
      // Preserva errori strutturati per il banner del wizard (422 VALIDATION_FAILED)
      if (Array.isArray(det?.errors) && det.errors.length > 0) {
        toast?.error(`${det.errors.length} campi da correggere`);
        return { ok: false, backendErrors: det.errors, message: det.message };
      }
      toast?.error(det?.message || det || "Errore invio");
      return null;
    } finally { setSubmitting(false); }
  }, [openMode, token, formData, contactData, wantsCyber, status, clearDraft]);

  return {
    loading, errCode, sezioni, wizard, compagnie, status,
    clientName, formData, setFormData, contactData, setContactData,
    wantsCyber, setWantsCyber, currentStep, setCurrentStep,
    saving, submitting, setField, submit, clearDraft,
  };
}
